import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { resolveUrl, BASE_URL } from '../utils/api';
import { getTheme } from '../utils/theme';

const theme = getTheme();

function storeAvatarUrl(url) {
  if (!url) return;
  const abs = url.startsWith('http') ? url : `${BASE_URL.replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`;
  localStorage.setItem('userAvatar', abs);
}

export default function Login() {
  const navigate = useNavigate();
  
  // Immediate redirect if already logged in (prevents back-button issues)
  useEffect(() => {
    if (localStorage.getItem('token')) {
      navigate('/random', { replace: true });
    }
  }, [navigate]);

  const [form, setForm] = useState({ username: '', password: '' });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestNameStatus, setGuestNameStatus] = useState(null);
  const [showLogin, setShowLogin] = useState(false); // Toggle between Guest and Login

  useEffect(() => {
    if (!guestName) {
      setGuestNameStatus(null);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      try {
        const res = await api.get(`/users/check-username/?username=${guestName}`);
        setGuestNameStatus({ available: res.data.is_available, suggestions: res.data.suggestions });
      } catch (err) {
        setGuestNameStatus(null);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [guestName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await api.post('/login/', form);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('username', res.data.username);
      localStorage.setItem('userId', res.data.id);
      localStorage.setItem('isGuest', res.data.is_guest ? 'true' : 'false');
      localStorage.setItem('is_staff', res.data.is_staff ? 'true' : 'false');
      localStorage.setItem('is_superuser', res.data.is_superuser ? 'true' : 'false');
      if (res.data.avatar) storeAvatarUrl(res.data.avatar);
      navigate('/random', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleGuestLogin = async () => {
    setError(''); setLoading(true);
    try {
      const res = await api.post('/guest-login/', { display_name: guestName.trim() || undefined });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('username', res.data.username);
      localStorage.setItem('userId', res.data.id);
      localStorage.setItem('isGuest', 'true');
      navigate('/random-chat', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Guest login failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        {/* Logo Section */}
        <div style={S.header}>
          {theme.brandLogo.startsWith('/') ? (
            <img src={theme.brandLogo} alt={theme.brandName} style={S.logoImg} />
          ) : (
            <h1 style={S.logoText}>{theme.brandLogo}</h1>
          )}
          <p style={S.tagline}>The world's favorite random chat platform</p>
        </div>

        {!showLogin ? (
          /* GUEST LOGIN VIEW (Default) */
          <div style={S.content}>
            <h2 style={S.title}>Guest Login</h2>
            <p style={S.description}>Start chatting instantly without an account</p>
            
            <div style={S.inputWrapper}>
              <input
                style={S.input}
                placeholder="Enter your nickname (optional)"
                value={guestName}
                onChange={e => setGuestName(e.target.value.replace(/ /g, '_'))}
                onKeyDown={e => e.key === 'Enter' && handleGuestLogin()}
                autoFocus
              />
              {guestName && guestNameStatus && (
                <div style={S.statusBox}>
                  {guestNameStatus.available ? (
                    <span style={{ color: '#4ade80' }}>✓ Name available</span>
                  ) : (
                    <>
                      <span style={{ color: '#f87171' }}>⚠ Name taken. Try:</span>
                      <div style={S.suggestionRow}>
                        {guestNameStatus.suggestions?.slice(0, 3).map(s => (
                          <button key={s} style={S.suggestionBtn} onClick={() => setGuestName(s)}>{s}</button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <button style={S.primaryBtn} onClick={handleGuestLogin} disabled={loading}>
              {loading ? 'Joining Chat...' : 'Start Chatting Now'}
            </button>

            <div style={S.divider}>
              <div style={S.line}></div>
              <span style={S.or}>Existing User?</span>
              <div style={S.line}></div>
            </div>

            <div style={S.buttonGroup}>
              <button style={S.secondaryBtn} onClick={() => setShowLogin(true)}>
                Login to Account
              </button>
              <Link to="/register" style={S.outlineBtn}>
                Create New Account
              </Link>
            </div>
          </div>
        ) : (
          /* MEMBER LOGIN VIEW */
          <div style={S.content}>
            <h2 style={S.title}>Login</h2>
            <p style={S.description}>Access your friends, profile, and history</p>

            <form onSubmit={handleSubmit} style={S.form}>
              <input
                style={S.input}
                placeholder="Username"
                value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value.replace(/ /g, '_') }))}
                required autoComplete="username"
              />
              <input
                type="password"
                style={S.input}
                placeholder="Password"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                required autoComplete="current-password"
              />
              {error && <p style={S.error}>{error}</p>}
              <button style={S.primaryBtn} disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div style={S.divider}>
              <div style={S.line}></div>
              <span style={S.or}>Prefer Guest Mode?</span>
              <div style={S.line}></div>
            </div>

            <button style={S.highlightBtn} onClick={() => setShowLogin(false)}>
              ✨ Back to Guest Login
            </button>

            <div style={S.footer}>
              New here? <Link to="/register" style={S.link}>Create an account</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    background: '#0a0a0f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Inter', system-ui, sans-serif",
    color: '#e2e8f0'
  },
  card: {
    background: '#111118',
    borderRadius: '28px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.05)',
    textAlign: 'center'
  },
  header: { marginBottom: '32px' },
  logoImg: { height: '50px', marginBottom: '12px', objectFit: 'contain' },
  logoText: { fontSize: '32px', fontWeight: 900, background: theme.colors.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 },
  tagline: { fontSize: '13px', color: '#64748b', fontWeight: 500, marginTop: '8px' },
  
  content: { display: 'flex', flexDirection: 'column', gap: '20px' },
  title: { fontSize: '24px', fontWeight: 800, color: '#f1f5f9', margin: 0 },
  description: { fontSize: '14px', color: '#94a3b8', margin: '-10px 0 10px' },
  
  inputWrapper: { textAlign: 'left' },
  input: {
    width: '100%',
    padding: '16px',
    borderRadius: '14px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.2)',
    color: '#fff',
    fontSize: '15px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.2s',
    '&:focus': { borderColor: theme.colors.primary }
  },
  
  statusBox: { fontSize: '12px', marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' },
  suggestionRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' },
  suggestionBtn: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: theme.colors.primary,
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer'
  },

  primaryBtn: {
    width: '100%',
    padding: '16px',
    borderRadius: '14px',
    border: 'none',
    background: theme.colors.gradient,
    color: '#fff',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: `0 8px 24px rgba(${theme.colors.primaryRGB}, 0.3)`,
    transition: 'transform 0.1s'
  },
  
  highlightBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: '14px',
    border: `1px solid rgba(${theme.colors.primaryRGB}, 0.4)`,
    background: `rgba(${theme.colors.primaryRGB}, 0.1)`,
    color: '#fff',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s'
  },

  divider: { display: 'flex', alignItems: 'center', margin: '20px 0', gap: '12px' },
  line: { flex: 1, height: '1px', background: 'rgba(255,255,255,0.05)' },
  or: { fontSize: '12px', color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' },
  
  buttonGroup: { display: 'flex', flexDirection: 'column', gap: '12px' },
  secondaryBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: '14px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.03)',
    color: '#f1f5f9',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  outlineBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: '14px',
    border: `1px solid rgba(${theme.colors.primaryRGB}, 0.2)`,
    background: 'none',
    color: theme.colors.primary,
    fontSize: '14px',
    fontWeight: 600,
    textDecoration: 'none',
    boxSizing: 'border-box'
  },
  
  form: { display: 'flex', flexDirection: 'column', gap: '14px' },
  error: { color: '#ef4444', fontSize: '13px', margin: '0' },
  backBtn: { background: 'none', border: 'none', color: '#64748b', fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: '10px' },
  
  footer: { fontSize: '13px', color: '#64748b', marginTop: '10px' },
  link: { color: theme.colors.primary, textDecoration: 'none', fontWeight: 700 }
};